import { SharePointListClient, odataEscape, type ListItem } from '../SharePointListClient';

/**
 * `01.ResumenOperaciones` — mixed Ventas + Cotizaciones master.
 * `Tipo_RO` distinguishes the two; status transitions follow the sales state
 * machine documented in the plan (Cotizado → Aceptado → … → Cerrado).
 *
 * Amounts are stored as numbers in SharePoint; the money-typed columns
 * (`MontoTotalCobro_RO`, `TotalCotizacion_RO`, …) come back as JavaScript
 * numbers — no Spanish-comma parsing needed. Dates, however, are stored as
 * `dd/mm/yyyy` strings for backwards compatibility with PowerApps.
 */
export type ResumenOperacionesFields = {
  Title?: string | null;
  NrCotizacion_RO?: string | null;
  IDCliente_RO?: string | null;
  Cliente_RO?: string | null;
  Correo_RO?: string | null;
  Vendedor_RO?: string | null;
  Tipo_RO?: 'Venta' | 'Cotizacion' | string | null;
  Status_RO?: string | null;
  Aceptado_RO?: 'SI' | null | string;
  Moneda_RO?: 'ARS' | 'MXN' | 'USD' | string | null;
  /**
   * MX-only Nova flag. Server-computed on every save: `true` when the
   * sale meets the Nova-flow conditions (currency = MXN AND total >
   * `Limite Nova` parameter). Returned as `'SI'` / `'NO'` and `null` on
   * AR rows. Read-only — inbound writes are ignored by the backend.
   */
  FNova_RO?: 'SI' | 'NO' | null;
  /**
   * MX Nova "horno usado" flag — `true` when the venta sits below the
   * `costo_contable × 1.05 × 1.16` threshold. Server-computed on every
   * breakdown recompute; surfaced here so the Ventas list can render a
   * red "Horno usado" pill next to the Total cell. Always false / null
   * on AR + non-Nova MX ventas.
   */
  FacturaInternaUsado_RO?: boolean | null;
  TotalCotizacion_RO?: number | null;
  MontoTotalCobro_RO?: number | null;
  /** Recargo MercadoPago por cuotas (snapshot al guardar Pagos). */
  RecargoMpCuotas_RO?: number | null;
  RecargoMpPct_RO?: number | string | null;
  RecargoMpMonto_RO?: number | string | null;
  Subtotal_RO?: number | null;
  IVA21_RO?: number | null;
  // SharePoint encodes `.` in column internal names as `_x002e_`, so the
  // Graph API returns and accepts the field under this key. PowerApps hid
  // the encoding; we have to use it verbatim.
  IVA10_x002e_5_RO?: number | null;
  Fecha_RO?: string | null;
  FechaPago_RO?: string | null;
  MesAnio_RO?: string | null;
  MesCreacion_RO?: string | null;
  AnioCreacion_RO?: string | null;
  /**
   * Internal column name on the SharePoint list is `PagoCargado_P`
   * even though the display name is `PagoCargado_RO` — a legacy
   * quirk from when the column was first created. Graph uses the
   * internal name on both reads and writes, so we expose it under
   * `PagoCargado_P` here.
   */
  PagoCargado_P?: 'SI' | null | string;
  // Pagos-screen invoice breakdown (numeric values stored as strings in
  // SharePoint, consistent with the rest of this list).
  PorcentajeFactura_RO?: string | null;
  MontoFactura_RO?: string | null;
  IVAaCobrar_RO?: string | null;
  TotalFactura_RO?: string | null;
  MontoCobroEfectivo_RO?: string | null;

  // Mantenimiento module — post-payment fulfillment chain.
  // Set by Confirmar Despacho; Status_RO advances to 'Pendiente Puesta Marcha'.
  Despachado_RO?: 'SI' | null | string;
  DireccionCompletaDespacho_RO?: string | null;
  RecibeDespacho_RO?: string | null;
  ObsDespacho_RO?: string | null;
  SerieDespachoCargados_RO?: string | null;
  NroDespachoConfirmado_RO?: string | null;
  /** Flipped to 'SI' after the 3 Puesta Marcha videos upload. */
  PuestaMarchaCargada_RO?: 'SI' | null | string;
  /** Flipped to 'SI' after the Certificado de Garantía PDF uploads. */
  CertificadoGarantiaCargado_RO?: 'SI' | null | string;

  /** JSON array (string) of drive item ids for Despacho photos. See M2. */
  DespachoFotoIds_RO?: string | null;
  /** JSON-encoded array of file-type labels parallel to
   *  `DespachoFotoIds_RO` (Remito · DNI Frente · DNI Dorso ·
   *  Autorización · Fotos del Retiro · Otros). Null on legacy rows
   *  uploaded before the column existed. */
  DespachoFotoTipos_RO?: string | null;
  /** Graph drive item id for the Puesta Marcha "instalación" video. */
  VideoInstalacionDriveItemID_RO?: string | null;
  /** Graph drive item id for the Puesta Marcha "llama" video. Blank when eléctrico. */
  VideoLlamaDriveItemID_RO?: string | null;
  /** Graph drive item id for the Puesta Marcha "regulador" video. Blank when eléctrico. */
  VideoReguladorDriveItemID_RO?: string | null;
  /** Graph drive item id for the optional Puesta Marcha multimedia file. */
  VideoMultimediaDriveItemID_RO?: string | null;
  /** Graph drive item id for the Certificado de Garantía PDF. */
  CertificadoDriveItemID_RO?: string | null;
};

export type ResumenOperacionesItem = ListItem<ResumenOperacionesFields>;

export const resumenOperacionesClient = new SharePointListClient<ResumenOperacionesFields>(
  'ResumenOperaciones',
);

/**
 * Fetch every row. The gallery filters client-side — matches how the
 * Configuración module handles its lists. The list should stay well
 * under the 10 000 ceiling enforced by the backend.
 */
export async function listAllOperaciones(): Promise<ResumenOperacionesItem[]> {
  return resumenOperacionesClient.listItems({ orderBy: 'fields/Modified desc' });
}

export async function listOperacionesByVendedor(vendedor: string) {
  return resumenOperacionesClient.listItems({
    filter: `fields/Vendedor_RO eq ${odataEscape(vendedor)}`,
    orderBy: 'fields/Modified desc',
  });
}

export async function listOperacionesByStatus(statuses: string[]) {
  if (!statuses.length) return [];
  const clause = statuses.map((s) => `fields/Status_RO eq ${odataEscape(s)}`).join(' or ');
  return resumenOperacionesClient.listItems({
    filter: clause,
    orderBy: 'fields/Modified desc',
  });
}
